/**
 * KPI API Service - Fleet Premium
 *
 * Servico para buscar dados reais da API e calcular KPIs
 * Integra com routePlanService para obter planos de rota
 */

import {
  getRoutePlans,
  getRoutePlanSummary,
} from '../../common/services/routePlanService';
import {
  calculateAllKPIs,
  calculateDeliveryKPIs,
  calculateEfficiencyKPIs,
  calculateCostKPIs,
  calculateFleetKPIs,
  calculateDriverKPIs,
  getEmptyKPIs,
  generateMockKPIs,
  KPI_PERIODS,
} from './kpiService';

// ============================================================================
// HELPERS - Funcoes auxiliares de datas
// ============================================================================

/**
 * Retorna intervalo de datas baseado no periodo
 * @param {string} period - Periodo (today, week, month, quarter, year)
 * @returns {object} { from: Date, to: Date }
 */
export const getDateRangeForPeriod = (period) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let from;
  const to = new Date(today);
  to.setHours(23, 59, 59, 999);

  switch (period) {
    case KPI_PERIODS.TODAY:
      from = new Date(today);
      break;

    case KPI_PERIODS.WEEK:
      from = new Date(today);
      from.setDate(today.getDate() - 7);
      break;

    case KPI_PERIODS.MONTH:
      from = new Date(today);
      from.setMonth(today.getMonth() - 1);
      break;

    case KPI_PERIODS.QUARTER:
      from = new Date(today);
      from.setMonth(today.getMonth() - 3);
      break;

    case KPI_PERIODS.YEAR:
      from = new Date(today);
      from.setFullYear(today.getFullYear() - 1);
      break;

    default:
      from = new Date(today);
      from.setDate(today.getDate() - 7);
  }

  return { from, to };
};

/**
 * Formata data para ISO string (apenas data)
 * @param {Date} date - Data
 * @returns {string} Data formatada
 */
const formatDateISO = (date) => {
  return date.toISOString().split('T')[0];
};

// ============================================================================
// API FETCHING
// ============================================================================

/**
 * Busca planos de rota para um periodo
 * @param {string} period - Periodo
 * @param {object} options - Opcoes adicionais
 * @returns {Promise<Array>} Lista de planos com detalhes
 */
export const fetchRoutePlansForPeriod = async (period, options = {}) => {
  const { from, to } = getDateRangeForPeriod(period);

  const params = {
    from: formatDateISO(from),
    to: formatDateISO(to),
    ...options,
  };

  try {
    const plans = await getRoutePlans(params);

    // Buscar detalhes completos de cada plano
    const detailedPlans = await Promise.all(
      plans.map(async (plan) => {
        try {
          const summary = await getRoutePlanSummary(plan.id);
          return {
            ...plan,
            stops: summary.stops || [],
            vehicles: summary.vehicles || [],
          };
        } catch (error) {
          console.warn(`Failed to fetch summary for plan ${plan.id}:`, error);
          return plan;
        }
      })
    );

    return detailedPlans;
  } catch (error) {
    console.error('Failed to fetch route plans:', error);
    throw error;
  }
};

/**
 * Busca lista de veiculos
 * @returns {Promise<Array>} Lista de veiculos
 */
export const fetchVehicles = async () => {
  try {
    const response = await fetch('/api/devices');
    if (!response.ok) {
      throw new Error(`Failed to fetch vehicles: ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    console.error('Failed to fetch vehicles:', error);
    return [];
  }
};

/**
 * Busca lista de motoristas
 * @returns {Promise<Array>} Lista de motoristas
 */
export const fetchDrivers = async () => {
  try {
    const response = await fetch('/api/drivers');
    if (!response.ok) {
      throw new Error(`Failed to fetch drivers: ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    console.error('Failed to fetch drivers:', error);
    return [];
  }
};

// ============================================================================
// KPI CALCULATIONS WITH API DATA
// ============================================================================

/**
 * Busca e calcula todos os KPIs para um periodo
 * @param {string} period - Periodo
 * @param {object} costConfig - Configuracao de custos (opcional)
 * @returns {Promise<object>} KPIs calculados
 */
export const fetchKPIs = async (period = KPI_PERIODS.WEEK, costConfig = null) => {
  try {
    // Buscar dados em paralelo
    const [plans, vehicles, drivers] = await Promise.all([
      fetchRoutePlansForPeriod(period),
      fetchVehicles(),
      fetchDrivers(),
    ]);

    // Se nao houver planos, retornar dados vazios
    if (plans.length === 0) {
      console.info('No route plans found for period');
      return {
        ...getEmptyKPIs(),
        meta: {
          ...getEmptyKPIs().meta,
          isEmpty: true,
          period,
        },
      };
    }

    // Enriquecer veiculos com nomes de motoristas
    const vehiclesWithDrivers = vehicles.map((v) => ({
      ...v,
      driverName: drivers.find((d) => d.id === v.driverId)?.name || null,
    }));

    // Calcular todos os KPIs
    const kpis = calculateAllKPIs(plans, vehiclesWithDrivers, costConfig);

    // Adicionar nomes de veiculos e motoristas aos rankings
    if (kpis.fleet?.topVehicles) {
      kpis.fleet.topVehicles = kpis.fleet.topVehicles.map((v) => {
        const device = vehicles.find((d) => d.id === v.id);
        return {
          ...v,
          name: device?.name || `Veiculo ${v.id}`,
        };
      });
    }

    if (kpis.drivers?.topDrivers) {
      kpis.drivers.topDrivers = kpis.drivers.topDrivers.map((d) => {
        const driver = drivers.find((dr) => dr.id === d.id);
        return {
          ...d,
          name: driver?.name || `Motorista ${d.id}`,
        };
      });
    }

    // Gerar dados de tendencia (ultimos 7 dias)
    const trendData = generateTrendData(plans);

    return {
      ...kpis,
      trends: trendData,
      meta: {
        ...kpis.meta,
        isDemo: false,
        period,
        vehicleCount: vehicles.length,
        driverCount: drivers.length,
      },
    };
  } catch (error) {
    console.error('Failed to fetch KPIs:', error);

    // Retornar dados vazios em caso de erro
    return {
      ...getEmptyKPIs(),
      meta: {
        ...getEmptyKPIs().meta,
        isEmpty: true,
        error: error.message,
        period,
      },
    };
  }
};

/**
 * Gera dados de tendencia baseado nos planos
 * @param {Array} plans - Lista de planos
 * @returns {object} Dados de tendencia
 */
const generateTrendData = (plans) => {
  // Agrupar planos por dia
  const byDay = {};

  plans.forEach((plan) => {
    const date = new Date(plan.date || plan.createdAt);
    const key = formatDateISO(date);

    if (!byDay[key]) {
      byDay[key] = [];
    }
    byDay[key].push(plan);
  });

  // Calcular metricas por dia
  const deliveryTrend = [];
  const costTrend = [];

  Object.entries(byDay)
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([date, dayPlans]) => {
      const deliveryKPIs = calculateDeliveryKPIs(dayPlans);
      const costKPIs = calculateCostKPIs(dayPlans);

      deliveryTrend.push({ date, value: deliveryKPIs.completed });
      costTrend.push({ date, value: costKPIs.totalCost });
    });

  return {
    deliveries: deliveryTrend,
    costs: costTrend,
  };
};

// ============================================================================
// SPECIALIZED KPI FETCHING
// ============================================================================

/**
 * Busca apenas KPIs de entregas
 * @param {string} period - Periodo
 * @returns {Promise<object>} KPIs de entregas
 */
export const fetchDeliveryKPIs = async (period = KPI_PERIODS.WEEK) => {
  try {
    const plans = await fetchRoutePlansForPeriod(period);
    if (plans.length === 0) {
      return getEmptyKPIs().deliveries;
    }
    return calculateDeliveryKPIs(plans);
  } catch (error) {
    console.error('Failed to fetch delivery KPIs:', error);
    return getEmptyKPIs().deliveries;
  }
};

/**
 * Busca apenas KPIs de eficiencia
 * @param {string} period - Periodo
 * @returns {Promise<object>} KPIs de eficiencia
 */
export const fetchEfficiencyKPIs = async (period = KPI_PERIODS.WEEK) => {
  try {
    const plans = await fetchRoutePlansForPeriod(period);
    if (plans.length === 0) {
      return getEmptyKPIs().efficiency;
    }
    return calculateEfficiencyKPIs(plans);
  } catch (error) {
    console.error('Failed to fetch efficiency KPIs:', error);
    return getEmptyKPIs().efficiency;
  }
};

/**
 * Busca apenas KPIs de custos
 * @param {string} period - Periodo
 * @param {object} costConfig - Configuracao de custos
 * @returns {Promise<object>} KPIs de custos
 */
export const fetchCostKPIs = async (period = KPI_PERIODS.WEEK, costConfig = null) => {
  try {
    const plans = await fetchRoutePlansForPeriod(period);
    if (plans.length === 0) {
      return getEmptyKPIs().costs;
    }
    return calculateCostKPIs(plans, costConfig);
  } catch (error) {
    console.error('Failed to fetch cost KPIs:', error);
    return getEmptyKPIs().costs;
  }
};

/**
 * Busca apenas KPIs de frota
 * @param {string} period - Periodo
 * @returns {Promise<object>} KPIs de frota
 */
export const fetchFleetKPIs = async (period = KPI_PERIODS.WEEK) => {
  try {
    const [plans, vehicles] = await Promise.all([
      fetchRoutePlansForPeriod(period),
      fetchVehicles(),
    ]);
    if (plans.length === 0) {
      return getEmptyKPIs().fleet;
    }
    return calculateFleetKPIs(plans, vehicles);
  } catch (error) {
    console.error('Failed to fetch fleet KPIs:', error);
    return getEmptyKPIs().fleet;
  }
};

/**
 * Busca apenas KPIs de motoristas
 * @param {string} period - Periodo
 * @returns {Promise<object>} KPIs de motoristas
 */
export const fetchDriverKPIs = async (period = KPI_PERIODS.WEEK) => {
  try {
    const [plans, drivers] = await Promise.all([
      fetchRoutePlansForPeriod(period),
      fetchDrivers(),
    ]);
    if (plans.length === 0) {
      return getEmptyKPIs().drivers;
    }
    const kpis = calculateDriverKPIs(plans);

    // Enriquecer com nomes
    if (kpis.topDrivers) {
      kpis.topDrivers = kpis.topDrivers.map((d) => {
        const driver = drivers.find((dr) => dr.id === d.id);
        return {
          ...d,
          name: driver?.name || `Motorista ${d.id}`,
        };
      });
    }

    return kpis;
  } catch (error) {
    console.error('Failed to fetch driver KPIs:', error);
    return getEmptyKPIs().drivers;
  }
};

// ============================================================================
// COMPARISON / TREND ANALYSIS
// ============================================================================

/**
 * Busca KPIs com comparacao ao periodo anterior
 * @param {string} period - Periodo atual
 * @param {object} costConfig - Configuracao de custos
 * @returns {Promise<object>} KPIs com comparacao
 */
export const fetchKPIsWithComparison = async (period = KPI_PERIODS.WEEK, costConfig = null) => {
  const currentKPIs = await fetchKPIs(period, costConfig);

  // Determinar periodo anterior
  let previousPeriod;
  switch (period) {
    case KPI_PERIODS.TODAY:
      previousPeriod = KPI_PERIODS.TODAY; // Ontem seria custom
      break;
    case KPI_PERIODS.WEEK:
      previousPeriod = KPI_PERIODS.WEEK;
      break;
    case KPI_PERIODS.MONTH:
      previousPeriod = KPI_PERIODS.MONTH;
      break;
    default:
      previousPeriod = period;
  }

  // Buscar KPIs do periodo anterior (simplificado)
  // Em producao, deveria calcular o intervalo exato do periodo anterior
  const previousKPIs = await fetchKPIs(previousPeriod, costConfig);

  // Calcular diferencas
  const comparison = {
    deliveries: {
      completionRateChange:
        currentKPIs.deliveries.completionRate - previousKPIs.deliveries.completionRate,
      onTimeRateChange:
        currentKPIs.deliveries.onTimeRate - previousKPIs.deliveries.onTimeRate,
      totalChange:
        currentKPIs.deliveries.total - previousKPIs.deliveries.total,
    },
    costs: {
      totalCostChange:
        currentKPIs.costs.totalCost - previousKPIs.costs.totalCost,
      costPerKmChange:
        currentKPIs.costs.costPerKm - previousKPIs.costs.costPerKm,
    },
    efficiency: {
      optimizationChange:
        currentKPIs.efficiency.optimizationSavingsPercent -
        previousKPIs.efficiency.optimizationSavingsPercent,
    },
  };

  return {
    current: currentKPIs,
    previous: previousKPIs,
    comparison,
  };
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Date helpers
  getDateRangeForPeriod,
  // Fetching
  fetchRoutePlansForPeriod,
  fetchVehicles,
  fetchDrivers,
  // KPI calculations
  fetchKPIs,
  fetchDeliveryKPIs,
  fetchEfficiencyKPIs,
  fetchCostKPIs,
  fetchFleetKPIs,
  fetchDriverKPIs,
  // Comparison
  fetchKPIsWithComparison,
};
